// src/components/Header/WorkspaceSwitcher.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Menu, MenuItem, ListItemIcon, ListItemText, Typography, Divider } from '@mui/material';
import { ChevronDown, Check, Briefcase } from 'lucide-react';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { fetchWorkspace, fetchWorkspaces } from '../../store/workspaceSlice';
import { clearCurrentBoard } from '../../store/boardSlice';

// Matches this app's real design tokens (src/styles/abstracts/_variables.scss,
// src/theme/theme.ts) rather than one-off guesses, so this control reads as
// part of the same system as the neighboring logo and "See Plan" button
// instead of a bolted-on extra.
const COLOR_PRIMARY = '#0073ea';
const COLOR_TEXT_PRIMARY = '#323338';
const COLOR_TEXT_SECONDARY = '#4b5563';
const COLOR_BG_TERTIARY = '#f5f6f8';
const COLOR_BORDER_LIGHT = '#e6e9ef';

/**
 * Always-visible "which workspace am I in" indicator - the app previously
 * had no persistent indicator of this anywhere outside the sidebar's
 * workspace list, so switching context (or landing on a board-agnostic
 * shortcut route) gave no confirmation of which workspace was active.
 *
 * Built on MUI's Menu (Portal-based, positions itself via Popper) rather
 * than a hand-rolled fixed-position dropdown - the latter turned out
 * fragile to ancestor stacking/clipping quirks. Menu is already the
 * proven pattern for every other dropdown in this app (see TeamsPage's
 * TeamOverflowMenu).
 *
 * Deliberately neutral (no brand blue) even though it sits next to the
 * blue "See Plan" button - this is a context control, not a call to
 * action, and staying neutral keeps See Plan as the one accented thing
 * in this row instead of splitting attention between two blue pills.
 */
export const WorkspaceSwitcher: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentWorkspace, workspaces } = useAppSelector((state) => state.workspaces);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (workspaces.length === 0) {
      dispatch(fetchWorkspaces());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = (workspaceId: string) => {
    if (workspaceId !== currentWorkspace?.id) {
      dispatch(fetchWorkspace(workspaceId));
      // Board-scoped pages (Sprint Board/Dashboard/Management, BoardPage)
      // key off state.boards.currentBoard, not currentWorkspace - it
      // belongs to the workspace being left and is no longer valid context
      // here. Clearing it and landing on the workspace dashboard is what
      // makes the switch actually take effect on whatever page you're on,
      // instead of silently continuing to show the old workspace's board.
      dispatch(clearCurrentBoard());
      navigate('/dashboard');
    }
    setAnchorEl(null);
  };

  if (!currentWorkspace) {
    return null;
  }

  return (
    <>
      <Button
        onClick={(e) => setAnchorEl(e.currentTarget)}
        title="Switch workspace"
        startIcon={<Briefcase size={16} />}
        endIcon={
          <ChevronDown
            size={16}
            style={{ transform: open ? 'rotate(180deg)' : undefined, transition: 'transform 150ms ease-in-out' }}
          />
        }
        sx={{
          textTransform: 'none',
          fontSize: '0.875rem',
          fontWeight: 500,
          fontFamily: 'inherit',
          lineHeight: 1.5,
          color: COLOR_TEXT_PRIMARY,
          bgcolor: COLOR_BG_TERTIARY,
          border: `1px solid ${COLOR_BORDER_LIGHT}`,
          borderRadius: '8px',
          px: 2,
          py: 1,
          maxWidth: 200,
          transition: 'all 150ms ease-in-out',
          '&:hover': {
            bgcolor: COLOR_BORDER_LIGHT,
            transform: 'translateY(-1px)',
            boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
          },
          '& .MuiButton-startIcon, & .MuiButton-endIcon': { color: COLOR_TEXT_SECONDARY },
        }}
      >
        <Typography
          component="span"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' }}
        >
          {currentWorkspace.name}
        </Typography>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { minWidth: 240, mt: 0.5, borderRadius: '8px' } } }}
      >
        <Typography
          sx={{
            display: 'block',
            px: 2,
            py: 1,
            color: COLOR_TEXT_SECONDARY,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontSize: '0.6875rem',
          }}
        >
          Workspaces
        </Typography>
        <Divider />
        {workspaces.length === 0 && (
          <MenuItem disabled>
            <ListItemText primary="No workspaces found" />
          </MenuItem>
        )}
        {workspaces.map((workspace) => (
          <MenuItem
            key={workspace.id}
            onClick={() => handleSelect(workspace.id)}
            selected={workspace.id === currentWorkspace.id}
            sx={{ fontSize: '0.875rem', py: 1.25 }}
          >
            <ListItemText
              primary={workspace.name}
              primaryTypographyProps={{ noWrap: true, sx: { maxWidth: 220, fontSize: 'inherit' } }}
            />
            {workspace.id === currentWorkspace.id && (
              <ListItemIcon sx={{ minWidth: 'auto', ml: 2 }}>
                <Check size={16} color={COLOR_PRIMARY} />
              </ListItemIcon>
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
